import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EFonts, EHovers } from 'libs/styles/styling';
import styled, { CSSProperties, css } from 'styled-components/macro';

interface Props {
    icon?: {
        iconSet?: IconPrefix;
        iconName: IconName;
        iconSpin?: boolean;
        iconRotation?: number;
        iconSize?: number;
    };
    text?: string;
    highlighted?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    disabled?: boolean;
    className?: string;
    style?: CSSProperties;
    children?: JSX.Element;
}

export const ActionButton = (props: Props): JSX.Element => {
    return (
        <S_ActionButton
            {...conditionalScaleAnimation(!props.disabled, 1.05, 0.9)}
            className={props.className}
            style={props.style}
            $highlighted={props.highlighted ?? false}
            $disabled={props.disabled}
            onClick={(e) => {
                if (!props.disabled) props.onClick?.(e);
            }}
            $fixedWidth={props.icon && !props.text}
        >
            {props.icon && (
                <S_Icon
                    icon={[props.icon.iconSet ?? 'fal', props.icon.iconName]}
                    $fontSize={props.icon?.iconSize}
                    $rotation={props.icon.iconRotation}
                    spin={props.icon.iconSpin ?? false}
                />
            )}
            {props.icon && props.text && <S_Space />}
            {props.text && <S_Text>{props.text}</S_Text>}
            {props.children}
        </S_ActionButton>
    );
};

const S_ActionButton = styled(motion.div)<{
    $fixedWidth: boolean;
    $highlighted: boolean;
    $disabled: boolean;
}>`
    flex-shrink: 0;
    min-height: 30px;
    font-size: 13px;
    display: flex;
    height: fit-content;
    justify-content: center;
    align-items: center;
    border-radius: 7px;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    user-select: none;
    height: 40px;
    box-sizing: border-box;
    ${(props) => {
        if (props.$fixedWidth) {
            return css`
                width: 40px;
            `;
        } else {
            return css`
                width: fit-content;
                padding: 8px 15px;
            `;
        }
    }}

    border: 1px solid #dadada;
    background: ${(props) => (props.$highlighted ? '#f4f4f4' : '#f5f5f5')};
    box-shadow: ${(props) =>
        props.$highlighted
            ? 'none'
            : '0px 0px 20px 0px #dedede inset, 0px 4px 4px 0px rgba(0, 0, 0, 0.03)'};
    &:hover {
        box-shadow: ${EHovers.LIGHT1};
    }
`;

const S_Icon = styled(FontAwesomeIcon)<{ $rotation?: number; $fontSize?: number }>`
    color: #141c54;
    font-size: ${(props) => props.$fontSize ?? 17}px;
    width: 20px;
    ${(props) => {
        if (props.$rotation) {
            return css`
                transform: rotate(${props.$rotation}deg);
            `;
        }
    }}
`;

const S_Text = styled.div`
    color: #141c54;
    font-family: ${EFonts.Regular};
`;

const S_Space = styled.div`
    width: 14px;
`;
